/**
 * Keeps every tab of one app on the same session.
 *
 * Better Auth's client already refetches the session when another tab writes its
 * `better-auth.message` storage event, but it only writes one for sign-out and profile
 * updates. Signing in, verifying a code, coming back from an OAuth provider and switching
 * organisation all leave the other tabs on the screen they were showing. This plugin watches
 * the session atom and, when who is signed in (or into which organisation) changes, posts on
 * that same channel. The last state seen is kept in localStorage so a tab that refetches and
 * finds what another tab announced stays quiet instead of announcing it back.
 */
import type { BetterAuthClientPlugin } from 'better-auth/client'
import { getGlobalBroadcastChannel } from 'better-auth/client'
import { onNotify } from 'nanostores'

export interface SessionSnapshot {
	data: { user: { id: string }; session: { activeOrganizationId?: string | null } } | null
	error: { status?: number } | null
	isPending: boolean
	isRefetching: boolean
}

export interface TabSyncEnvironment {
	read: () => string | null
	write: (key: string) => void
	announce: () => void
}

const ANONYMOUS = 'anonymous'

export function sessionKey(snapshot: SessionSnapshot): string | null {
	if (snapshot.isPending || snapshot.isRefetching) return null
	if (snapshot.error !== null && snapshot.error.status !== 401) return null
	if (snapshot.data === null) return ANONYMOUS
	return `${snapshot.data.user.id}:${snapshot.data.session.activeOrganizationId ?? ''}`
}

export function announceSessionChanges(environment: TabSyncEnvironment): (snapshot: SessionSnapshot) => void {
	return (snapshot) => {
		const key = sessionKey(snapshot)
		if (key === null || key === environment.read()) return
		environment.write(key)
		environment.announce()
	}
}

export function browserEnvironment(storageKey: string): TabSyncEnvironment | null {
	if (typeof window === 'undefined') return null
	return {
		read: () => {
			try {
				return window.localStorage.getItem(storageKey)
			} catch {
				return null
			}
		},
		write: (key) => {
			try {
				window.localStorage.setItem(storageKey, key)
			} catch {}
		},
		announce: () => getGlobalBroadcastChannel().post({ event: 'session', data: { trigger: 'getSession' }, clientId: storageKey }),
	}
}

export function tabSyncClient(storageKey: string, environment: TabSyncEnvironment | null = browserEnvironment(storageKey)) {
	return {
		id: 'tab-sync',
		getActions: (_$fetch, $store) => {
			const session = $store.atoms.session
			if (environment !== null && session !== undefined) {
				const announce = announceSessionChanges(environment)
				onNotify(session, () => announce(session.value))
			}
			return {}
		},
	} satisfies BetterAuthClientPlugin
}
